import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { toast } from "sonner";

const formatErr = (d) => {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || JSON.stringify(e)).join(", ");
  return String(d);
};

export default function Auth({ mode = "login" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/account";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") await register(name, email, password);
      else await login(email, password);
      toast.success(mode === "register" ? "Account created" : "Signed in");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-md mx-auto px-6 py-16" data-testid={`auth-${mode}-page`}>
        <p className="text-xs uppercase tracking-[0.3em] text-primary">KicksGuard Members</p>
        <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-3">
          {mode === "register" ? "CREATE ACCOUNT" : "WELCOME BACK"}<span className="text-primary">.</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-3">
          {mode === "register"
            ? "Save your orders, reorder in one tap, and get first look at drops."
            : "Sign in to see your order history and reorder your favorites."}
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          {mode === "register" && (
            <input
              data-testid="auth-name-input"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="w-full border border-border px-4 py-3 text-sm outline-none focus:border-foreground"
            />
          )}
          <input
            data-testid="auth-email-input"
            required
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full border border-border px-4 py-3 text-sm outline-none focus:border-foreground"
          />
          <input
            data-testid="auth-password-input"
            required
            type="password"
            minLength={6}
            autoComplete={mode === "register" ? "new-password" : "current-password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password (min 6 chars)"
            className="w-full border border-border px-4 py-3 text-sm outline-none focus:border-foreground"
          />
          <button
            type="submit"
            data-testid="auth-submit-button"
            disabled={busy}
            className="w-full bg-primary text-white py-3 text-sm uppercase tracking-wider hover:bg-foreground transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            {mode === "register" ? "Create Account" : "Sign In"}
          </button>
        </form>
        <p className="text-sm text-muted-foreground mt-6 text-center">
          {mode === "register" ? (
            <>Already have an account?{" "}
              <Link to="/login" data-testid="auth-switch-to-login" className="text-foreground font-medium hover:text-primary transition-colors">Sign in</Link>
            </>
          ) : (
            <>New to KicksGuard?{" "}
              <Link to="/register" data-testid="auth-switch-to-register" className="text-foreground font-medium hover:text-primary transition-colors">Create an account</Link>
            </>
          )}
        </p>
        <p className="text-xs text-muted-foreground mt-6 text-center">Guest checkout is still available — no account required.</p>
      </div>
    </div>
  );
}
